/**
 * Sync design / Revise design — one proposal-review-apply dialog with two
 * explicit modes:
 *
 *   pending-sync       — confirmation modal for changes already made since the
 *                        last build; maps ripple effects and applies proposals.
 *   freeform-revision  — describe-a-change flow (textarea → map ripple effects
 *                        → review diffs → apply). Reached from the wand on
 *                        authored docs and Co-Designer recommendations.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { BuildUpdateStatus, DesignConflict, DesignProject } from '@volley/crucible-ipc-types';
import { MarkdownContent } from '../../components/MarkdownContent';
import { useCrucibleClient } from '../../transport/context.js';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { diffLines, type DiffRowType } from './lib/line-diff';
import {
  applyProposals,
  buildLensBlock,
  conflictLoserSectionIds,
  describeConflictResolutions,
  detectConflicts,
  draftSections,
  planRipple,
  StaleRippleProposalError,
  synthesizePendingIdea,
  type LensRef,
  type RipplePlanEntry,
  type RippleProposal,
} from './lib/hammer-flow';
import { buildHiddenReferenceContext, formatReferenceUsage } from './lib/reference-selection';

/** Tailwind classes for each unified-diff row kind. */
const DIFF_ROW_CLASS: Record<DiffRowType, string> = {
  add: 'bg-success/12 text-success',
  del: 'bg-error/12 text-error',
  context: 'text-crucible-muted',
};
const DIFF_ROW_SIGN: Record<DiffRowType, string> = { add: '+', del: '-', context: ' ' };
const EMPTY_LENS_IDS: LensRef[] = [];
const EMPTY_CHANGED_SECTION_IDS: string[] = [];

export type SyncDesignDialogMode =
  | { kind: 'pending-sync' }
  | {
      kind: 'freeform-revision';
      initialInstruction?: string;
      source?: 'wand' | 'review-recommendation';
    };

export interface SyncDesignDialogProps {
  gameId: string;
  project: DesignProject;
  open: boolean;
  mode: SyncDesignDialogMode;
  /** Pending-mode source of truth (summary + changed ids). */
  buildStatus?: BuildUpdateStatus | null;
  onOpenChange: (open: boolean) => void;
  /** Selected lens ids to fold into the system prompt (optional). */
  lensIds?: LensRef[];
  /** Called after proposals are applied so the host can refresh. */
  onApplied?: () => void;
}

type Step =
  // freeform
  | 'idea'
  | 'working'
  | 'review'
  // pending
  | 'mapping'
  | 'conflicts'
  | 'confirm'
  | 'applying';

export function SyncDesignDialog({
  gameId,
  project,
  open,
  mode,
  buildStatus,
  onOpenChange,
  lensIds = EMPTY_LENS_IDS,
  onApplied,
}: SyncDesignDialogProps): ReactElement {
  const client = useCrucibleClient();
  const [step, setStep] = useState<Step>('idea');
  const [idea, setIdea] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [proposals, setProposals] = useState<RippleProposal[]>([]);
  const [ripples, setRipples] = useState<RipplePlanEntry[]>([]);
  const [referenceNames, setReferenceNames] = useState<string[]>([]);
  // Pre-sync: contradictions between the edited docs + the designer's chosen
  // winner per conflict (conflict id → winning section id).
  const [conflicts, setConflicts] = useState<DesignConflict[]>([]);
  const [resolutions, setResolutions] = useState<Record<string, string>>({});
  // Invalidates in-flight async work when the dialog closes or re-opens.
  const runToken = useRef(0);

  const isPendingMode = mode.kind === 'pending-sync';
  const initialInstruction =
    mode.kind === 'freeform-revision' ? (mode.initialInstruction ?? '') : '';
  const title = isPendingMode ? 'Sync design' : 'Revise design';
  const changedCount = buildStatus?.changedCount ?? 0;
  const changedSectionIds = buildStatus?.changedSectionIds ?? EMPTY_CHANGED_SECTION_IDS;
  const summary = buildStatus?.summary;
  const latestPendingContext = useRef({ gameId, project, lensIds, summary, changedSectionIds });
  // Pending sync inputs can be rebuilt by the workspace while the dialog is already open.
  latestPendingContext.current = { gameId, project, lensIds, summary, changedSectionIds };

  const sectionTitle = useCallback(
    (sectionId: string): string =>
      project.sections.find((s) => s.id === sectionId)?.title ?? sectionId,
    [project.sections],
  );

  /**
   * Maps the real ripple effects of already-made edits in pending mode.
   *
   * When the designer has resolved contradictions between the edited docs, the
   * chosen winners are folded into the idea (so the ripple propagates the right
   * value) and the losing edited sections are allowed back into the ripple
   * targets so they get corrected.
   *
   * Args:
   *   resolved: The detected conflicts + chosen winners, or null when there are
   *     none to apply.
   *
   * Returns:
   *   Promise that resolves after the mapping state is updated.
   *
   * Raises:
   *   None. Mapping failures are surfaced through dialog error state.
   */
  const planPendingRipples = useCallback(
    async (
      resolved: { conflicts: DesignConflict[]; resolutions: Record<string, string> } | null,
    ) => {
      const {
        gameId: pendingGameId,
        project: pendingProject,
        lensIds: pendingLensIds,
        summary: pendingSummary,
        changedSectionIds: pendingChangedSectionIds,
      } = latestPendingContext.current;
      if (!pendingSummary) return;
      const token = ++runToken.current;
      setStep('mapping');
      setError(null);
      try {
        const resolutionBlock = resolved
          ? describeConflictResolutions(resolved.conflicts, resolved.resolutions)
          : '';
        const pendingIdea = synthesizePendingIdea(pendingSummary) + resolutionBlock;
        setIdea(pendingIdea);
        setReferenceNames([]);
        const lensBlock = await buildLensBlock(client, pendingLensIds);
        const planned = await planRipple(client, {
          gameId: pendingGameId,
          project: pendingProject,
          idea: pendingIdea,
          lensBlock,
        });
        if (token !== runToken.current) return;
        // The user's own edits are the source, not ripple targets — except a
        // section that LOST a resolved conflict, which must be corrected.
        const loserIds = resolved
          ? conflictLoserSectionIds(resolved.conflicts, resolved.resolutions)
          : [];
        setRipples(
          planned.filter(
            (p) =>
              !pendingChangedSectionIds.includes(p.sectionId) || loserIds.includes(p.sectionId),
          ),
        );
        setStep('confirm');
      } catch (e: unknown) {
        if (token !== runToken.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setStep('confirm');
      }
    },
    [client],
  );

  /**
   * Pending-mode entry: first check whether the edited docs contradict each
   * other. If they do, stop and surface the conflicts for resolution; otherwise
   * go straight to ripple planning.
   */
  const mapPendingRipples = useCallback(async () => {
    const {
      gameId: pendingGameId,
      project: pendingProject,
      lensIds: pendingLensIds,
      summary: pendingSummary,
      changedSectionIds: pendingChangedSectionIds,
    } = latestPendingContext.current;
    if (!pendingSummary) return;
    const token = ++runToken.current;
    setStep('mapping');
    setError(null);
    setConflicts([]);
    setResolutions({});
    try {
      const lensBlock = await buildLensBlock(client, pendingLensIds);
      if (token !== runToken.current) return;
      const found = await detectConflicts(client, {
        gameId: pendingGameId,
        project: pendingProject,
        editedSectionIds: pendingChangedSectionIds,
        lensBlock,
      });
      if (token !== runToken.current) return;
      if (found.length > 0) {
        setConflicts(found);
        setStep('conflicts');
        return;
      }
    } catch {
      // Conflict detection is best-effort — fall through to ripple planning.
      if (token !== runToken.current) return;
    }
    await planPendingRipples(null);
  }, [planPendingRipples, client]);

  const allConflictsResolved = conflicts.every((c) => resolutions[c.id]);
  /** Conflicts resolved → fold the winners in and run the ripple pass. */
  const resolveConflictsAndSync = useCallback(() => {
    void planPendingRipples({ conflicts, resolutions });
  }, [conflicts, resolutions, planPendingRipples]);

  // (Re)initialise per open — the mode and prefill can differ every time.
  useEffect(() => {
    if (!open) {
      runToken.current += 1;
      return;
    }
    setError(null);
    setStatus('');
    setProposals([]);
    setRipples([]);
    setReferenceNames([]);
    setConflicts([]);
    setResolutions({});
    if (mode.kind === 'pending-sync') {
      void mapPendingRipples();
    } else {
      setIdea(initialInstruction);
      setStep('idea');
    }
    // Deliberately excludes pending buildStatus churn — one reset per open/prefill task.
  }, [open, mode.kind, initialInstruction, mapPendingRipples]);

  /** Pending mode: confirm → draft the ripple sections and write them. */
  const confirmSync = useCallback(async () => {
    const token = ++runToken.current;
    setStep('applying');
    setError(null);
    try {
      if (ripples.length > 0) {
        setStatus(`Updating ${ripples.length} section${ripples.length === 1 ? '' : 's'}…`);
        const lensBlock = await buildLensBlock(client, lensIds);
        const drafts = await draftSections(client, {
          gameId,
          project,
          idea,
          lensBlock,
          planned: ripples,
        });
        if (token !== runToken.current) return;
        await applyProposals(client, { gameId, project, proposals: drafts });
      }
      if (token !== runToken.current) return;
      onApplied?.();
      onOpenChange(false);
    } catch (e: unknown) {
      if (token !== runToken.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setStep('confirm');
    }
  }, [ripples, lensIds, gameId, project, idea, onApplied, onOpenChange, client]);

  /** Freeform mode: map ripple effects, then draft every planned section. */
  const analyze = useCallback(async () => {
    if (!idea.trim()) return;
    const token = ++runToken.current;
    setStep('working');
    setError(null);
    try {
      const lensBlock = await buildLensBlock(client, lensIds);
      const referenceContext = await buildHiddenReferenceContext(client, { gameId, intent: idea });
      const promptContext = lensBlock + referenceContext.block;
      if (token !== runToken.current) return;
      setReferenceNames(referenceContext.names);
      setStatus('Mapping ripple effects across the design…');
      const planned = await planRipple(client, { gameId, project, idea, lensBlock: promptContext });
      if (token !== runToken.current) return;
      if (planned.length === 0) {
        setError('No sections need to change for that idea.');
        setStep('idea');
        return;
      }
      setStatus(`Drafting ${planned.length} section${planned.length === 1 ? '' : 's'}…`);
      const drafts = await draftSections(client, {
        gameId,
        project,
        idea,
        lensBlock: promptContext,
        planned,
      });
      if (token !== runToken.current) return;
      setProposals(drafts);
      setStep('review');
    } catch (e: unknown) {
      if (token !== runToken.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setStep('idea');
    }
  }, [idea, lensIds, project, gameId, client]);

  const apply = useCallback(async () => {
    setStatus('Applying accepted changes…');
    try {
      await applyProposals(client, { gameId, project, proposals });
      onApplied?.();
      onOpenChange(false);
    } catch (e: unknown) {
      if (e instanceof StaleRippleProposalError) {
        setError(`${e.message}. No changes were applied.`);
        setStep('review');
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [proposals, project, gameId, onApplied, onOpenChange, client]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {error && (
          <div
            className="mb-3 rounded border border-error/25 bg-error/12 p-2 text-xs text-error"
            data-testid="sync-design-error"
          >
            {error}
          </div>
        )}

        {/* ---- Pending mode: contradictions between the edited docs (resolve first) ---- */}
        {step === 'conflicts' && (
          <div data-testid="sync-design-conflicts">
            <p className="mb-3 text-sm text-crucible-muted">
              These edited sections disagree with each other. Pick which one is right before syncing
              — the rest of the design will be brought in line with your choice.
            </p>
            <ul className="space-y-3">
              {conflicts.map((c) => (
                <li
                  key={c.id}
                  className="rounded border border-amber-500/40 bg-amber-500/[0.04] p-3"
                  data-testid={`sync-conflict-${c.id}`}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span
                      className="size-1.5 shrink-0 rounded-full bg-amber-300"
                      aria-hidden="true"
                    />
                    <span className="text-sm font-medium text-crucible-fg">{c.topic}</span>
                  </div>
                  {c.suggestion && (
                    <p className="mb-2 text-xs text-crucible-muted">{c.suggestion}</p>
                  )}
                  <div className="space-y-1.5">
                    {c.claims.map((claim) => (
                      <label
                        key={claim.sectionId}
                        className="flex cursor-pointer items-start gap-2 rounded border border-crucible-border bg-white/[0.02] px-2.5 py-1.5 text-sm"
                      >
                        <input
                          type="radio"
                          name={`conflict-${c.id}`}
                          className="mt-0.5"
                          checked={resolutions[c.id] === claim.sectionId}
                          onChange={() =>
                            setResolutions((prev) => ({ ...prev, [c.id]: claim.sectionId }))
                          }
                        />
                        <span className="min-w-0">
                          <span className="font-medium text-crucible-fg">{claim.sectionTitle}</span>
                          <span className="text-crucible-muted"> — {claim.claim}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                data-testid="sync-conflicts-resolve"
                onClick={resolveConflictsAndSync}
                disabled={!allConflictsResolved}
              >
                Resolve &amp; continue
              </Button>
            </div>
          </div>
        )}

        {/* ---- Pending mode: confirmation of the changes since the last build ---- */}
        {(step === 'mapping' || step === 'confirm' || step === 'applying') && (
          <div data-testid="sync-design-pending">
            <p className="mb-3 text-sm text-crucible-muted">
              {changedCount} section{changedCount === 1 ? '' : 's'} changed{' '}
              {buildStatus?.hasSnapshot ? 'since the last build' : 'in the design docs'}. Syncing
              brings the rest of the design in line before you send to the build.
            </p>
            {summary && (
              <ul className="mb-3 space-y-1.5" data-testid="sync-design-changed-rows">
                {[
                  ...summary.sections.changed.map((t) => [t, 'edited'] as const),
                  ...summary.sections.added.map((t) => [t, 'new section'] as const),
                  ...summary.sections.removed.map((t) => [t, 'removed'] as const),
                  ...summary.screens.changed.map((t) => [t, 'screen edited'] as const),
                  ...summary.screens.added.map((t) => [t, 'new screen'] as const),
                  ...summary.screens.removed.map((t) => [t, 'screen removed'] as const),
                ].map(([title, what]) => (
                  <li
                    key={`${title}-${what}`}
                    className="flex items-center gap-2 rounded border border-crucible-border bg-white/[0.02] px-2.5 py-1.5 text-sm"
                  >
                    <span
                      className="size-1.5 shrink-0 rounded-full bg-amber-300"
                      aria-hidden="true"
                    />
                    <span className="font-medium text-crucible-fg">{title}</span>
                    <span className="text-xs text-crucible-muted">— {what}</span>
                  </li>
                ))}
              </ul>
            )}
            {step === 'mapping' && (
              <p
                className="flex items-center gap-2 py-1 text-xs text-amber-200"
                data-testid="sync-design-mapping"
              >
                <span
                  className="size-1.5 animate-pulse rounded-full bg-amber-300"
                  aria-hidden="true"
                />
                Mapping ripple effects across the design…
              </p>
            )}
            {step === 'confirm' && (
              <p className="text-xs text-crucible-muted" data-testid="sync-design-ripple-line">
                {ripples.length > 0 ? (
                  <>
                    Will also update:{' '}
                    <span className="text-crucible-fg">
                      {ripples.map((r) => sectionTitle(r.sectionId)).join(' · ')}
                    </span>
                  </>
                ) : (
                  'No other sections need updating — the rest of the design already agrees.'
                )}
              </p>
            )}
            {step === 'applying' && (
              <p className="py-1 text-xs text-crucible-muted" data-testid="sync-design-applying">
                {status || 'Working…'}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                data-testid="sync-design-confirm"
                onClick={() => void confirmSync()}
                disabled={step !== 'confirm'}
              >
                {ripples.length > 0 || step !== 'confirm' ? 'Sync design' : 'Mark as synced'}
              </Button>
            </div>
          </div>
        )}

        {/* ---- Freeform mode: describe one change → map → review diffs ---- */}
        {step === 'idea' && (
          <div data-testid="sync-design-idea">
            <p className="mb-2 text-sm text-crucible-muted">
              Describe one design revision. The agent maps everywhere it ripples — rules, copy,
              screens, the plan — before writing anything.
            </p>
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              rows={4}
              placeholder="e.g. Add a steal mechanic where the trailing team can wager points…"
              className="w-full resize-none rounded border border-crucible-border bg-transparent p-2 text-sm text-crucible-fg outline-none"
            />
            <div className="mt-3 flex justify-end">
              <Button
                size="sm"
                data-testid="sync-design-map"
                onClick={() => void analyze()}
                disabled={!idea.trim()}
              >
                Map ripple effects
              </Button>
            </div>
          </div>
        )}

        {step === 'working' && (
          <div
            className="py-6 text-center text-sm text-crucible-muted"
            data-testid="sync-design-working"
          >
            {status || 'Working…'}
          </div>
        )}

        {step === 'review' && (
          <div data-testid="sync-design-review">
            <p className="mb-3 text-xs text-crucible-muted">
              {proposals.filter((p) => p.accepted).length} of {proposals.length} section
              {proposals.length === 1 ? '' : 's'} selected to apply.
            </p>
            <p
              className="mb-3 text-xs text-crucible-muted"
              data-testid="sync-design-references-used"
            >
              {formatReferenceUsage(referenceNames)}
            </p>
            <ul className="space-y-4">
              {proposals.map((p) => (
                <li key={p.sectionId} className="rounded border border-crucible-border p-2">
                  <label className="flex items-center gap-2 text-sm text-crucible-fg">
                    <input
                      type="checkbox"
                      checked={p.accepted}
                      disabled={!p.applyable}
                      onChange={(e) =>
                        setProposals((prev) =>
                          prev.map((x) =>
                            x.sectionId === p.sectionId ? { ...x, accepted: e.target.checked } : x,
                          ),
                        )
                      }
                    />
                    <span className="font-medium">{p.sectionTitle}</span>
                    <span className="text-crucible-muted">— {p.diff}</span>
                  </label>
                  {p.error && <p className="mt-1 text-xs text-error">{p.error}</p>}
                  {/* Unified red-delete / green-add diff (no diff dep, §7.5). */}
                  <div className="mt-2 rounded bg-crucible-bg-elevated p-2">
                    <div className="mb-1 f-meta text-crucible-muted">Changes</div>
                    <div className="max-h-60 overflow-y-auto rounded font-mono f-accent leading-snug">
                      {diffLines(p.originalBody, p.newBody).map((row, idx) => (
                        <div key={idx} className={`flex gap-1.5 px-1 ${DIFF_ROW_CLASS[row.type]}`}>
                          <span aria-hidden className="select-none opacity-50">
                            {DIFF_ROW_SIGN[row.type]}
                          </span>
                          <span className="min-w-0 whitespace-pre-wrap break-words">
                            {row.text || ' '}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {(p.impacts || p.risks || p.benefits) && (
                    <div className="mt-2 grid grid-cols-3 gap-2 f-accent">
                      {[
                        ['Impacts', p.impacts],
                        ['Risks', p.risks],
                        ['Benefits', p.benefits],
                      ].map(([label, val]) =>
                        val ? (
                          <div key={label}>
                            <div className="mb-0.5 uppercase text-crucible-muted">{label}</div>
                            <MarkdownContent content={val} />
                          </div>
                        ) : null,
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep('idea');
                  setProposals([]);
                  setError(null);
                }}
              >
                Start over
              </Button>
              <Button
                size="sm"
                data-testid="sync-design-apply"
                onClick={() => void apply()}
                disabled={!proposals.some((p) => p.accepted)}
              >
                Apply selected changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
